import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";

import { misc, neutral } from "app/themes/colors";
import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";
import { ReportTableCell } from "features/reports/components/report-table-cell/report-table-cell.component";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import SearchPreview from "./search-preview.component";

// This row lives in the Insert Scribe dialog, which sits above MUI's tooltip layer.
const TOOLTIP_POPPER_PROPS = {
  sx: {
    zIndex: (theme: Theme) => getScribeModalPopoverZIndex(theme.zIndex.snackbar)
  }
};

const TOGGLE_BUTTON_SX = {
  border: `solid 1px ${neutral[600]}`,
  width: 28,
  height: 28
};

interface ScribeListRowPreviewProps {
  row: AmbientScribe;
  dSearch: string;
  previewText: string;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onSelect: (row: AmbientScribe | null) => void;
}

function PreviewBody({
  row,
  dSearch,
  previewText
}: Pick<ScribeListRowPreviewProps, "row" | "dSearch" | "previewText">) {
  if (row.preview) {
    return <SearchPreview preview={row.preview} search={dSearch} />;
  }
  if (!previewText) return null;
  return (
    <Typography variant="body1" lineHeight={1.6} pb={1}>
      {previewText}
    </Typography>
  );
}

/**
 * The row under a recording that previews its text: the search match while searching, otherwise
 * the first of summary, scribe and transcript, behind an expand toggle.
 */
export default function ScribeListRowPreview({
  row,
  dSearch,
  previewText,
  isSelected,
  isExpanded,
  onToggleExpanded,
  onSelect
}: ScribeListRowPreviewProps) {
  // Show expanded if search has value OR user manually expanded
  const shouldShowExpanded = Boolean(dSearch) || isExpanded;

  return (
    <TableRow
      className="table-row-group"
      onClick={() => {
        onSelect(row);
      }}
      sx={{ boxShadow: "none" }}
    >
      <ReportTableCell
        colSpan={3}
        sx={{
          background: isSelected ? misc.selectedSubMenuItem : undefined,
          paddingTop: 0,
          paddingBottom: shouldShowExpanded ? 2 : 1,
          borderBottom: "solid 2px rgba(0,0,0, 0.15)"
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          mt={shouldShowExpanded ? 0 : -0.5}
        >
          <Box width="100%">
            {shouldShowExpanded && (
              <Box width="100%">
                <PreviewBody
                  row={row}
                  dSearch={dSearch}
                  previewText={previewText}
                />
              </Box>
            )}
          </Box>

          {!dSearch && (
            <Box pl={2} mr={-1}>
              <Tooltip
                title={shouldShowExpanded ? "Collapse" : "Expand"}
                disableInteractive
                PopperProps={TOOLTIP_POPPER_PROPS}
              >
                <IconButton sx={TOGGLE_BUTTON_SX} onClick={onToggleExpanded}>
                  {shouldShowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </ReportTableCell>
    </TableRow>
  );
}
